import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FinancialPeriod {
  id: string;
  tenant_id: string;
  period_name: string;
  start_date: string;
  end_date: string;
  status: 'open' | 'closed' | 'locked';
  opening_balance: number;
  closing_balance: number | null;
  system_closing_balance: number | null;
  total_receivables: number;
  total_payables: number;
  total_invoiced: number;
  total_received: number;
  total_credit_notes: number;
  total_debit_notes: number;
  total_write_offs: number;
  total_discounts: number;
  notes: string | null;
  opened_by_id: string;
  opened_at: string;
  closed_by_id: string | null;
  closed_at: string | null;
  locked_by_id: string | null;
  locked_at: string | null;
}

export interface FinancialPeriodsResponse {
  data: FinancialPeriod[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface OpenPeriodInput {
  period_name: string;
  start_date: string;
  end_date: string;
  notes?: string;
  opening_balance?: number | null;
}

export interface ClosePeriodInput {
  periodId: string;
  notes?: string;
  adjusted_closing_balance?: number | null;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const financialPeriodKeys = {
  all: ['billing', 'financial-periods'] as const,
  list: (status?: string) => [...financialPeriodKeys.all, 'list', status] as const,
  current: () => [...financialPeriodKeys.all, 'current'] as const,
  detail: (id: string) => [...financialPeriodKeys.all, 'detail', id] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch list of financial periods with optional status filter.
 */
export function useFinancialPeriods(status?: 'open' | 'closed' | 'locked') {
  return useQuery({
    queryKey: financialPeriodKeys.list(status),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean } & FinancialPeriodsResponse>(
        '/billing/financial-periods',
        { params: { status: status || undefined, pageSize: 50 } },
      );
      return response.data;
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * Fetch the currently open financial period.
 */
export function useCurrentFinancialPeriod() {
  return useQuery({
    queryKey: financialPeriodKeys.current(),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: FinancialPeriod | null }>(
        '/billing/financial-periods/current',
      );
      return response.data.data;
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * Open a new financial period.
 */
export function useOpenPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: OpenPeriodInput) => {
      const response = await apiClient.post<{ success: boolean; data: FinancialPeriod }>(
        '/billing/financial-periods',
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialPeriodKeys.all });
    },
  });
}

/**
 * Close a financial period.
 */
export function useClosePeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ClosePeriodInput) => {
      const response = await apiClient.post<{ success: boolean; data: FinancialPeriod }>(
        `/billing/financial-periods/${input.periodId}/close`,
        {
          notes: input.notes || undefined,
          adjusted_closing_balance: input.adjusted_closing_balance ?? undefined,
        },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialPeriodKeys.all });
    },
  });
}

/**
 * Lock a closed period permanently.
 */
export function useLockPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (periodId: string) => {
      const response = await apiClient.post<{ success: boolean; data: FinancialPeriod }>(
        `/billing/financial-periods/${periodId}/lock`,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialPeriodKeys.all });
    },
  });
}

/**
 * Reopen a closed (non-locked) period.
 */
export function useReopenFinancialPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (periodId: string) => {
      const response = await apiClient.post<{ success: boolean; data: FinancialPeriod }>(
        `/billing/financial-periods/${periodId}/reopen`,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialPeriodKeys.all });
    },
  });
}

/**
 * Update a financial period (name, dates, notes).
 */
export function useUpdatePeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      periodId: string;
      period_name?: string;
      start_date?: string;
      end_date?: string;
      notes?: string | null;
    }) => {
      const { periodId, ...data } = input;
      const response = await apiClient.patch<{ success: boolean; data: FinancialPeriod }>(
        `/billing/financial-periods/${periodId}`,
        data,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialPeriodKeys.all });
    },
  });
}

/**
 * Delete the last financial period. Only the most recent period can be deleted.
 * Requires billing.delete_period permission.
 */
export function useDeletePeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (periodId: string) => {
      const response = await apiClient.delete<{ success: boolean; message: string }>(
        `/billing/financial-periods/${periodId}`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialPeriodKeys.all });
    },
  });
}
